
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOEnhanced } from '@/components/SEOEnhanced';
import { seoKeywords } from '@/utils/seoEnhanced';

const AboutUs = () => {
  const { language, t } = useLanguage();

  const stats = [
    { key: 'students_matched', number: '10,000+', label: 'Students Matched with Safe Housing', labelAr: 'طالب تم ربطهم بسكن آمن' },
    { key: 'verified_properties', number: '2,500+', label: 'Verified Properties Listed', labelAr: 'عقار معتمد مدرج' },
    { key: 'university_partnerships', number: '50+', label: 'University Partnerships', labelAr: 'شراكة جامعية' },
    { key: 'cities_covered', number: '15+', label: 'Cities Covered Across Egypt', labelAr: 'مدينة مغطاة في مصر' },
  ];

  const values = [
    {
      key: 'trust',
      title: 'Trust & Safety',
      titleAr: 'الثقة والأمان',
      description: 'Every listing is verified. Every student\'s well-being comes first.',
      descriptionAr: 'كل قائمة معتمدة. رفاهية كل طالب تأتي أولاً.'
    },
    {
      key: 'accessibility',
      title: 'Accessibility',
      titleAr: 'إمكانية الوصول',
      description: 'Affordable housing should not be a privilege. It\'s a right for every Egyptian student.',
      descriptionAr: 'السكن الميسور التكلفة يجب ألا يكون امتيازاً. إنه حق لكل طالب مصري.'
    },
    {
      key: 'innovation',
      title: 'Innovation with Purpose',
      titleAr: 'الابتكار بهدف',
      description: 'Technology built to serve real needs — simplifying the housing journey for both students and landlords.',
      descriptionAr: 'التكنولوجيا مبنية لخدمة الاحتياجات الحقيقية - تبسيط رحلة السكن للطلاب وأصحاب العقارات على حد سواء.'
    }
  ];

  return (
    <div className={`min-h-screen ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <SEOEnhanced
        title={language === 'ar' 
          ? 'عن مسكن - منصة الإسكان الطلابي الرائدة في مصر'
          : 'About Maskan - Leading Student Housing Platform in Egypt'
        }
        description={language === 'ar'
          ? 'تعرف على قصة مسكن وقيمنا في توفير حلول سكنية آمنة ومريحة للطلاب في جميع أنحاء مصر'
          : 'Learn about Maskan\'s story and our values in providing safe, comfortable housing solutions for students across Egypt'
        }
        keywords={seoKeywords.about}
        canonical="/about"
        structuredDataType="Organization"
        lang={language}
        breadcrumbs={[
          { name: language === 'ar' ? 'الرئيسية' : 'Home', url: 'https://maskan-student-nest.lovable.app/' },
          { name: language === 'ar' ? 'عنا' : 'About Us', url: 'https://maskan-student-nest.lovable.app/about' }
        ]}
      />
      
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            {t('about_maskan', 'عن مسكن', 'About Maskan')}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {language === 'ar' 
              ? 'إعادة تعريف السكن الطلابي في مصر'
              : 'Redefining Student Housing in Egypt'
            }
          </h1>
          <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
            {language === 'ar'
              ? 'مسكن هو منصة الإسكان الطلابي الرائدة في مصر، تم إطلاقه في عام 2025 لحل واحد من أكثر التحديات إلحاحاً في البلاد: السكن الآمن والميسور التكلفة والقابل للوصول لطلاب الجامعات.'
              : 'Maskan is Egypt\'s pioneering student housing platform, launched in 2025 to solve one of the country\'s most pressing challenges: safe, affordable, and accessible housing for university students.'
            }
          </p>
        </div>

        {/* Problem Statement */}
        <div className="mb-16">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-lg mb-6">
                {language === 'ar'
                  ? 'لعقود، عانى الطلاب في جميع أنحاء مصر من خيارات محدودة، وإيجارات متضخمة، وأصحاب عقارات غير موثوقين. أصحاب العقارات، بدورهم، لم يكن لديهم طريقة منظمة للوصول إلى السوق الطلابي. تم بناء مسكن لتغيير هذا.'
                  : 'For decades, students across Egypt struggled with limited choices, inflated rents, and unreliable landlords. Property owners, in turn, had no structured way to reach the student market. Maskan was built to change this.'
                }
              </p>
              <p className="text-lg font-semibold">
                {language === 'ar'
                  ? 'اليوم، مسكن أكثر من مجرد منصة. إنه نظام الإسكان الطلابي في مصر — يربط الطلاب بمنازل معتمدة ويمكّن أصحاب العقارات من فتح فرص جديدة.'
                  : 'Today, Maskan is more than a platform. It is Egypt\'s student housing ecosystem — connecting students with verified homes and empowering property owners to unlock new opportunities.'
                }
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Our Impact Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">
            {language === 'ar' ? 'تأثيرنا' : 'Our Impact'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <Card key={stat.key} className="text-center">
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-primary mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'ar' ? stat.labelAr : stat.label}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Our Story Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">
            {language === 'ar' ? 'قصتنا' : 'Our Story'}
          </h2>
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4 text-center">
                  {language === 'ar' ? 'مسكن وُلد في مصر، من أجل مصر.' : 'Maskan was born in Egypt, for Egypt.'}
                </h3>
                <p className="text-lg mb-6">
                  {language === 'ar'
                    ? 'رأينا الفجوة السكنية التي تواجه ملايين الطلاب. رأينا العائلات مجبرة على الدفع بما يفوق إمكانياتها، والطلاب مدفوعين إلى أماكن إقامة غير آمنة أو غير مناسبة. بنينا مسكن بمهمة واحدة: جعل السكن الطلابي قابلاً للوصول وشفافاً وآمناً.'
                    : 'We saw the housing gap facing millions of students. We saw families forced to pay beyond their means, and students pushed into unsafe or inconvenient accommodations. We built Maskan with one mission: to make student housing accessible, transparent, and secure.'
                  }
                </p>
                <p className="text-lg">
                  {language === 'ar'
                    ? 'من القاهرة إلى الإسكندرية، من أسيوط إلى المنصورة، يصبح مسكن بسرعة الخيار الموثوق للطلاب على مستوى البلاد. من خلال مزج الرؤية المحلية مع التكنولوجيا المتطورة، بنينا منصة مصممة حول التحديات والفرص الفريدة في مصر.'
                    : 'From Cairo to Alexandria, from Assiut to Mansoura, Maskan is rapidly becoming the trusted choice for students nationwide. By blending local insight with cutting-edge technology, we\'ve built a platform designed around Egypt\'s unique challenges and opportunities.'
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Our Values Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">
            {language === 'ar' ? 'قيمنا' : 'Our Values'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value) => (
              <Card key={value.key}>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4">
                    {language === 'ar' ? value.titleAr : value.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'ar' ? value.descriptionAr : value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Our Mission Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">
            {language === 'ar' ? 'مهمتنا' : 'Our Mission'}
          </h2>
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-lg">
                  {language === 'ar'
                    ? 'أن نكون منصة الإسكان الطلابي الرائدة في مصر — المعيار للسلامة والثقة والقدرة على تحمل التكاليف — وأن ننمو لنصبح أكبر شبكة إسكان في المنطقة، ونضع المعيار لكيفية عيش الطلاب ودراستهم وازدهارهم.'
                    : 'To be Egypt\'s leading student housing platform — the standard for safety, trust, and affordability — and to grow into the region\'s largest housing network, setting the benchmark for how students live, study, and thrive.'
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
